import { Modal } from '@doordash/component-modal'
import { SingleSelectGroup } from '@doordash/component-select-group'
import { Icon, StackChildren } from '@doordash/design-language'
import { kebabCase } from 'lodash'
import * as React from 'react'
import { connect } from 'react-redux'
import { i18nKeys } from 'translationTypes'
import { AbnGuideItem } from '..'
import * as events from '../../../../../../events'
import i18n from '../../../../../../i18n'
import * as DA from '../../../../../../typeModules/events'
import { isMobile } from '../../../../../../utilities/userAgent'
import { APPLY_ABN_LINK } from '../../AbnPage'
import { AbnStepByStep } from './config'
import { StepByStep } from './StepByStep'
import { VideoPanel } from './VideoPanel'

const ButtonText = {
  GET_AN_ABN: i18n.t(i18nKeys.backgroundCheck__abnHowTo__ctaButton),
  NEXT_STEP: i18n.t(i18nKeys.common__nextStep),
  PREVIOUS_STEP: i18n.t(i18nKeys.common__previousStep),
  DONE: i18n.t(i18nKeys.common__done),
}

// compute length of step-by-step enum
const totalSteps = Object.keys(AbnStepByStep).filter(step =>
  isNaN(Number(step))
).length

interface IAbnHowToProps {
  onClose: () => void
  trackBackgroundCheckSteps: (payload: unknown) => void
}

function AbnHowTo({ onClose, trackBackgroundCheckSteps }: IAbnHowToProps) {
  const [isVideo, setIsVideo] = React.useState(true)
  const [step, setStep] = React.useState(AbnStepByStep.ABN_APPLY)

  React.useEffect(() => {
    const stage = isVideo ? 'video' : step

    trackBackgroundCheckSteps({
      page: DA.BackgroundCheckPages.ABN,
      action: DA.EventActions.Rendered,
      value: `${AbnGuideItem.HOW_TO}-${stage}`,
    })
  }, [step, trackBackgroundCheckSteps, isVideo])

  const getPrimaryButtonText = () => {
    if (isVideo || step === AbnStepByStep.ABN_APPLY) {
      return ButtonText.GET_AN_ABN
    }

    return step === AbnStepByStep.DECLARATION
      ? ButtonText.DONE
      : ButtonText.NEXT_STEP
  }

  const getSecondaryButtonText = () => {
    if (isVideo) {
      return null
    }

    return step === AbnStepByStep.ABN_APPLY
      ? ButtonText.NEXT_STEP
      : ButtonText.PREVIOUS_STEP
  }

  const primaryButtonText = getPrimaryButtonText()
  const secondaryButtonText = getSecondaryButtonText()
  const trailingIcon =
    primaryButtonText === ButtonText.GET_AN_ABN && Icon.Types.LinkExternalLine

  const handlePrimaryClick = () => {
    trackBackgroundCheckSteps({
      page: DA.BackgroundCheckPages.ABN,
      action: DA.EventActions.Clicked,
      value: `${AbnGuideItem.HOW_TO}-${step}-${kebabCase(primaryButtonText)}`,
    })

    switch (primaryButtonText) {
      case ButtonText.GET_AN_ABN:
        return window.open(APPLY_ABN_LINK, '_blank')
      case ButtonText.NEXT_STEP:
        return setStep(step => step + 1)
      case ButtonText.DONE:
        return onClose()
      default:
        return null
    }
  }

  const handleSecondaryClick = () => {
    trackBackgroundCheckSteps({
      page: DA.BackgroundCheckPages.ABN,
      action: DA.EventActions.Clicked,
      value: `${AbnGuideItem.HOW_TO}-${step}-${kebabCase(secondaryButtonText)}`,
    })
    switch (secondaryButtonText) {
      case ButtonText.NEXT_STEP:
        return setStep(step => step + 1)
      case ButtonText.PREVIOUS_STEP:
        return setStep(step => step - 1)
      default:
        return null
    }
  }

  return (
    <Modal
      onClose={onClose}
      size={Modal.Sizes.XLarge}
      isFullScreenOnMobile
      title={i18n.t(i18nKeys.backgroundCheck__abnHowTo)}
      subtitle={i18n.t(i18nKeys.backgroundCheck__abnHowTo__description)}
      shouldRenderStickyHeader={false}
      closeButtonSide={Modal.CloseButtonSides.Leading}
      primaryAction={
        primaryButtonText
          ? {
              text: primaryButtonText,
              onClick: handlePrimaryClick,
              buttonProps: { trailingIcon, isInline: !isMobile },
            }
          : null
      }
      secondaryAction={
        secondaryButtonText
          ? {
              text: secondaryButtonText,
              onClick: handleSecondaryClick,
            }
          : null
      }
      useHairlineDividers
    >
      <StackChildren size={StackChildren.Sizes.XLarge}>
        <SingleSelectGroup
          accessibilityLabel="Video and step by step options"
          isInline
          options={[
            i18n.t(i18nKeys.common__video),
            i18n.t(i18nKeys.common__stepByStep),
          ]}
          defaultSelectedIndex={0}
          onChange={() => setIsVideo(isVideo => !isVideo)}
          type={SingleSelectGroup.Types.PrimaryPill}
          size={SingleSelectGroup.Sizes.Small}
        />
        {isVideo && <VideoPanel />}
        {!isVideo && <StepByStep step={step} total={totalSteps} />}
      </StackChildren>
    </Modal>
  )
}

const mapDispatchToProps = {
  trackBackgroundCheckSteps: events.trackBackgroundCheckSteps,
}

export default connect(null, mapDispatchToProps)(AbnHowTo)
